import { routePaths } from "@/router/routePaths"
import { CX_CORE_FEATURES_ENUM, featureCheck, type Features } from "@/common/util/featuresUtil"

export enum EnumWorkspaceId {
  CERTIFICATE_MANAGEMENT = "certificate-management",
  PCF_JUPITER = "pcf-jupiter",
  PCF_SATURN = "pcf-saturn",
  BLOCK_NOTIFICATIONS = "block-notifications",
  TRACEABILITY = "traceability",
  CYBER_SECURITY_METRICS = "cyber-security-metrics",
  ELECTRONIC_CONTROL_UNIT = "electronic-control-unit",
  BATTERY_PASSPORT = "battery-passport",
  SIMPLE_FILE_EXCHANGE = "simple-file-exchange"
}

export interface IWorkspace {
  id: EnumWorkspaceId
  textKey: string
  /** Image path under `public/`, or a Scale icon tag (`scale-icon-...`) — see `isScaleIcon`. */
  icon: string
  /** Landing route of the workspace. Absent while the module does not exist yet. */
  routeName?: string
  feature?: Features
  isComingSoon?: boolean
}

export const workspaces: IWorkspace[] = [
  {
    id: EnumWorkspaceId.CERTIFICATE_MANAGEMENT,
    textKey: "workspace.apps.names.certificateManagement",
    icon: "/assets/usecase-icons/certificate.svg",
    routeName: routePaths.applications.certificateManagement.pathName,
    feature: CX_CORE_FEATURES_ENUM.CERTIFICATE_MANAGEMENT
  },
  {
    id: EnumWorkspaceId.PCF_JUPITER,
    textKey: "workspace.apps.names.pcfJupiter",
    icon: "/assets/usecase-icons/pcf.svg",
    routeName: routePaths.applications.pcfExchange.pathName,
    feature: CX_CORE_FEATURES_ENUM.PCF_EXCHANGE
  },
  {
    id: EnumWorkspaceId.PCF_SATURN,
    textKey: "workspace.apps.names.pcfSaturn",
    icon: "/assets/usecase-icons/pcf.svg",
    routeName: routePaths.applications.pcfExchangeV9.pathName,
    feature: CX_CORE_FEATURES_ENUM.PCF_EXCHANGE
  },
  {
    id: EnumWorkspaceId.BLOCK_NOTIFICATIONS,
    textKey: "workspace.apps.names.blockNotifications",
    icon: "/assets/usecase-icons/block_notification.svg",
    routeName: routePaths.applications.blockNotifications.pathName,
    feature: CX_CORE_FEATURES_ENUM.BLOCK_NOTIFICATIONS
  },
  {
    id: EnumWorkspaceId.TRACEABILITY,
    textKey: "workspace.apps.names.traceability",
    icon: "/assets/usecase-icons/traceability.svg",
    routeName: routePaths.applications.traceability.pathName,
    feature: CX_CORE_FEATURES_ENUM.TRACEABILITY
  },
  {
    id: EnumWorkspaceId.CYBER_SECURITY_METRICS,
    textKey: "workspace.apps.names.cyberSecurityMetrics",
    icon: "/assets/usecase-icons/cybersecurity.svg",
    routeName: routePaths.applications.cyberSecurityKPIs.pathName,
    feature: CX_CORE_FEATURES_ENUM.CYBERSECURITY_KPIS
  },
  {
    id: EnumWorkspaceId.ELECTRONIC_CONTROL_UNIT,
    textKey: "workspace.apps.names.electronicControlUnit",
    icon: "/assets/usecase-icons/ecu.svg",
    routeName: routePaths.applications.electronicControlUnit.pathName,
    feature: CX_CORE_FEATURES_ENUM.ELECTRONIC_CONTROL_UNIT
  },
  {
    id: EnumWorkspaceId.BATTERY_PASSPORT,
    textKey: "workspace.apps.names.batteryPassport",
    icon: "/assets/usecase-icons/battery_pass.svg",
    isComingSoon: true
  },
  {
    id: EnumWorkspaceId.SIMPLE_FILE_EXCHANGE,
    textKey: "workspace.apps.names.simpleFileExchange",
    icon: "/assets/usecase-icons/simple_file_exchange.svg",
    isComingSoon: true
  }
]

export const defaultWorkspaceIcon = "scale-icon-content-applications"

export const isScaleIcon = (icon: string): boolean => icon.startsWith("scale-icon-")

/** Advanced is a rail mode, not a selectable app, so it carries no workspace id. */
export const advancedPanel: Pick<IWorkspace, "textKey" | "icon"> = {
  textKey: "workspace.apps.names.advanced",
  icon: "scale-icon-service-devices-service"
}

/** Apps the tenant is subscribed to, plus the not-yet-built ones shown as "coming soon". */
export const getAvailableWorkspaces = (): IWorkspace[] =>
  workspaces.filter((workspace) => workspace.isComingSoon || featureCheck(workspace.feature!))

/**
 * Route ownership, used only to seed the side nav on a hard load. Advanced is a
 * rail mode rather than a workspace, so its routes are tracked separately.
 */
const advancedRouteNames: string[] = [
  routePaths.advanced.children.policies.pathName,
  routePaths.advanced.children.contractAgreements.pathName,
  routePaths.advanced.children.contractDefinitions.pathName,
  routePaths.advanced.children.connectors.pathName,
  routePaths.advanced.children.assets.pathName,
  routePaths.advanced.children.uploadHistory.pathName,
  routePaths.advanced.children.simulateOffers.pathName,
  routePaths.advanced.children.providerTwinBrowser.pathName,
  routePaths.advanced.children.consumerTwinBrowser.pathName,
  routePaths.advanced.children.processLogs.pathName
]

export const isAdvancedRoute = (routeName: string): boolean =>
  advancedRouteNames.includes(routeName)

export const getWorkspaceIdForRoute = (routeName: string): EnumWorkspaceId | undefined => {
  const appRouteName = routeName.split(".")[0]
  return workspaces.find((workspace) => workspace.routeName === appRouteName)?.id
}
